import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";

const IMG = {
  hero: "/images/hero.jpg",
  landscape: "/images/landscape.jpg",
  olives: "/images/olives.jpg",
  grove: "/images/grove.jpg",
  pour: "/images/pour.jpg",
  bottle: "/images/bottle.jpg",
  reserveBand: "/images/reserve_band.jpg",
};

const HEADER_OFFSET = 72;

export default function Home() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    const target = location.state && location.state.scrollTo;
    if (!target) return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~1.3s at 30ms

    const attempt = () => {
      if (cancelled) return;
      const el = document.getElementById(target);
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
        window.scrollTo({ top: Math.max(0, y), behavior: "auto" });
      }
      attempts += 1;
      if (attempts < MAX_ATTEMPTS) {
        window.setTimeout(attempt, 30);
      } else {
        // Clear the state so future navigations don't re-scroll
        navigate(location.pathname, { replace: true, state: {} });
      }
    };
    window.setTimeout(attempt, 20);

    return () => {
      cancelled = true;
    };
  }, [location.state, location.pathname, navigate]);

  const values = t("values.items");
  const reserveItems = t("reserve.items");
  const stats = t("heritage.stats");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !consent) return;
    navigate("/thank_you");
  };

  return (
    <main style={{ backgroundColor: "#FAF8F5", color: "#1C1917" }} data-testid="home-page">
      {/* HERO */}
      <section className="border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10 py-16 lg:py-24">
          <div className="lg:col-span-6 flex flex-col justify-center">
            <div className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: "#8A6E1E" }}>
              {t("hero.overline")}
            </div>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight" style={{ color: "#1C241C" }}>
              {t("hero.title_line_1")}
              <br />
              <em className="not-italic" style={{ color: "#2D3B2D" }}>{t("hero.title_line_2")}</em>
              <br />
              <span style={{ color: "#8A6E1E" }}>{t("hero.title_line_3")}</span>
            </h1>
            <p className="mt-8 font-sans text-base leading-relaxed max-w-xl" style={{ color: "#57534E" }}>
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="#newsletter"
                data-testid="hero-cta-primary"
                className="rounded-sm px-6 py-3 font-sans text-sm tracking-wide text-center"
                style={{ backgroundColor: "#2D3B2D", color: "#FAF8F5" }}
              >
                {t("hero.cta_primary")}
              </a>
              <a
                href="#story"
                data-testid="hero-cta-secondary"
                className="rounded-sm border border-stone-400 px-6 py-3 font-sans text-sm tracking-wide text-center"
                style={{ color: "#1C241C" }}
              >
                {t("hero.cta_secondary")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-sm border border-stone-200">
              <img
                src={IMG.hero}
                alt={t("hero.image_alt")}
                loading="eager"
                className="h-full w-full object-cover"
              />
              <div
                className="absolute bottom-4 left-4 right-4 backdrop-blur-sm px-4 py-3 border border-stone-300"
                style={{ backgroundColor: "rgba(250,248,245,0.9)" }}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#8A6E1E" }}>
                  Greve in Chianti · Firenze
                </div>
                <div className="mt-1 font-serif text-lg" style={{ color: "#1C241C" }}>
                  43°35′N · 11°19′E
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section id="values" className="border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: "#8A6E1E" }}>
                {t("values.overline")}
              </div>
              <h2 className="mt-6 font-serif text-3xl lg:text-4xl leading-tight" style={{ color: "#1C241C" }}>
                {t("values.title")}
              </h2>
            </div>
            <div className="lg:col-span-8 grid gap-8 sm:grid-cols-2">
              {values.map((item) => (
                <div key={item.number} className="border-t border-stone-300 pt-6">
                  <div className="font-mono text-xs" style={{ color: "#8A6E1E" }}>
                    {item.number}
                  </div>
                  <div className="mt-3 font-serif text-xl" style={{ color: "#1C241C" }}>
                    {item.title}
                  </div>
                  <p className="mt-3 font-sans text-sm leading-relaxed" style={{ color: "#57534E" }}>
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="border-b border-stone-200" style={{ backgroundColor: "#F1EDE4" }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="aspect-[3/4] w-full overflow-hidden rounded-sm border border-stone-300">
              <img
                src={IMG.landscape}
                alt={t("story.image_alt")}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: "#8A6E1E" }}>
              {t("story.overline")}
            </div>
            <h2 className="mt-6 font-serif text-3xl lg:text-4xl leading-tight" style={{ color: "#1C241C" }}>
              {t("story.title")}
            </h2>
            <div className="mt-8 space-y-5 font-sans text-base leading-relaxed" style={{ color: "#3F3B36" }}>
              <p>{t("story.paragraph_1")}</p>
              <p>{t("story.paragraph_2")}</p>
              <p>{t("story.paragraph_3")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* RESERVE */}
      <section id="reserve" className="border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col justify-center">
            <div className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: "#8A6E1E" }}>
              {t("reserve.overline")}
            </div>
            <h2 className="mt-6 font-serif text-3xl lg:text-4xl leading-tight" style={{ color: "#1C241C" }}>
              {t("reserve.title")}
            </h2>
            <ul className="mt-8 space-y-6">
              {reserveItems.map((item, idx) => (
                <li key={idx} className="border-l-2 pl-5" style={{ borderColor: "#C59B27" }}>
                  <div className="font-serif text-lg" style={{ color: "#1C241C" }}>
                    {item.title}
                  </div>
                  <p className="mt-2 font-sans text-sm leading-relaxed" style={{ color: "#57534E" }}>
                    {item.text}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 aspect-[6/5]">
              <div className="col-span-4 row-span-4 overflow-hidden rounded-sm border border-stone-300">
                <img src={IMG.bottle} alt={t("reserve.image_alt")} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-2 row-span-3 overflow-hidden rounded-sm border border-stone-300">
                <img src={IMG.olives} alt="" className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-2 row-span-3 overflow-hidden rounded-sm border border-stone-300">
                <img src={IMG.pour} alt="" className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-6 row-span-2 overflow-hidden rounded-sm border border-stone-300">
                <img src={IMG.reserveBand} alt="" className="h-full w-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HERITAGE */}
      <section id="heritage" className="border-b border-stone-200" style={{ backgroundColor: "#1C241C", color: "#FAF8F5" }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: "#C59B27" }}>
              {t("heritage.overline")}
            </div>
            <h2 className="mt-6 font-serif text-3xl lg:text-4xl leading-tight" style={{ color: "#FAF8F5" }}>
              {t("heritage.title")}
            </h2>
            <p className="mt-8 font-sans text-base leading-relaxed" style={{ color: "#B8B1A3" }}>
              {t("heritage.paragraph")}
            </p>
            <dl className="mt-10 grid grid-cols-2 gap-6">
              {stats.map((s, i) => (
                <div key={i} className="border-t pt-4" style={{ borderColor: "#3A4A3A" }}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#C59B27" }}>
                    {s.label}
                  </dt>
                  <dd className="mt-2 font-serif text-3xl" style={{ color: "#FAF8F5" }}>
                    {s.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-7">
            <div className="aspect-[4/3] w-full overflow-hidden rounded-sm border" style={{ borderColor: "#3A4A3A" }}>
              <img
                src={IMG.grove}
                alt={t("heritage.image_alt")}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" style={{ backgroundColor: "#FAF8F5" }}>
        <div className="mx-auto max-w-4xl px-6 lg:px-10 py-20">
          <div className="text-center">
            <div className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: "#8A6E1E" }}>
              {t("newsletter.overline")}
            </div>
            <h2 className="mt-6 font-serif text-3xl lg:text-4xl leading-tight" style={{ color: "#1C241C" }}>
              {t("newsletter.title")}
            </h2>
            <p className="mt-6 font-sans text-base leading-relaxed max-w-2xl mx-auto" style={{ color: "#57534E" }}>
              {t("newsletter.subtitle")}
            </p>
          </div>

          <form
            className="mt-12 grid gap-5 max-w-xl mx-auto"
            onSubmit={handleSubmit}
            data-testid="newsletter-form"
            noValidate
          >
            <label className="block">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: "#8A6E1E" }}>
                {t("newsletter.name_label")}
              </span>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("newsletter.name_placeholder")}
                data-testid="newsletter-name-input"
                className="mt-2 w-full border border-stone-400 bg-white px-4 py-3 font-sans text-sm rounded-sm focus:outline-none focus:border-stone-800"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: "#8A6E1E" }}>
                {t("newsletter.email_label")}
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("newsletter.email_placeholder")}
                data-testid="newsletter-email-input"
                className="mt-2 w-full border border-stone-400 bg-white px-4 py-3 font-sans text-sm rounded-sm focus:outline-none focus:border-stone-800"
              />
            </label>
            <label className="flex items-start gap-3 font-sans text-sm" style={{ color: "#57534E" }}>
              <input
                type="checkbox"
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="newsletter-consent"
                className="mt-1"
              />
              <span>{t("newsletter.consent_label")}</span>
            </label>
            <button
              type="submit"
              data-testid="newsletter-submit"
              disabled={!name.trim() || !email.trim() || !consent}
              className="mt-2 rounded-sm px-6 py-3 font-sans text-sm tracking-wide disabled:opacity-50"
              style={{ backgroundColor: "#2D3B2D", color: "#FAF8F5" }}
            >
              {t("newsletter.submit")}
            </button>
            <p className="text-center font-sans text-xs" style={{ color: "#8A8478" }}>
              {t("newsletter.footnote")}
            </p>
          </form>
        </div>
      </section>
    </main>
  );
}
